import { supabase } from "./supabase";
import {
  validateWorkout,
  validateMealEntry,
  validateCustomMeal,
  validateRun,
  validateWeightEntry,
  validateServings,
} from "../utils/validate";

// =========== AUTH ===========

export async function signUp(email, password, username) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { username } },
  });
  if (error) throw error;

  // If email confirmation is enabled, session may be null
  if (!data.user) throw new Error("Sign-up failed");

  // Only insert profile if we have an active session (email confirmed or
  // confirmation disabled). When email confirmation is required, the profile
  // will be created on first sign-in via ensureProfile().
  if (data.session) {
    const { error: profileError } = await supabase.from("profiles").insert({
      id: data.user.id,
      username,
    });
    if (profileError) {
      // Username taken (unique constraint)
      if (profileError.code === "23505") {
        throw new Error("Username is already taken");
      }
      throw profileError;
    }
  }

  return data;
}

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  localStorage.clear();
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function deleteAccount() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not authenticated");

  const API_BASE = import.meta.env.VITE_API_URL || "";
  const res = await fetch(`${API_BASE}/api/delete-account`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to delete account");
  }

  // Clear all local data
  localStorage.clear();
  await supabase.auth.signOut();
}

export async function resetPassword(email) {
  // No redirectTo: Supabase sends the user to the project's Site URL with the
  // recovery token. Passing redirectTo requires it to be in the Redirect URLs
  // allowlist (exact match), which is easy to get wrong and yields
  // "requested path is invalid". Relying on Site URL avoids that entirely.
  const { error } = await supabase.auth.resetPasswordForEmail(email);
  if (error) throw error;
}

// Set a new password for the currently-authenticated (or recovery) session.
export async function updatePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

export async function ensureProfile(userId, username) {
  const existing = await getProfile(userId);
  if (existing) return existing;
  const { data, error } = await supabase
    .from("profiles")
    .insert({ id: userId, username })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getProfile(userId) {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .single();
  // PGRST116 = no rows found — profile doesn't exist yet
  if (error && error.code === "PGRST116") return null;
  if (error) throw error;
  return data;
}

// =========== WORKOUTS ===========

export async function fetchWorkouts(userId) {
  const { data, error } = await supabase
    .from("workouts")
    .select("*")
    .eq("user_id", userId)
    .order("start_time", { ascending: false });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    name: r.name,
    templateId: r.template_id,
    exerciseBlocks: r.exercise_blocks,
    startTime: r.start_time,
    endTime: r.end_time,
    caloriesBurned: r.calories_burned,
    rpe: r.rpe,
    soreness: r.soreness,
  }));
}

export async function insertWorkout(userId, workout) {
  validateWorkout(workout);
  const { error } = await supabase.from("workouts").insert({
    id: workout.id,
    user_id: userId,
    name: workout.name,
    template_id: workout.templateId,
    exercise_blocks: workout.exerciseBlocks,
    start_time: workout.startTime,
    end_time: workout.endTime,
    calories_burned: workout.caloriesBurned ?? null,
  });
  if (error) throw error;
}

export async function deleteWorkout(workoutId) {
  const { error } = await supabase
    .from("workouts")
    .delete()
    .eq("id", workoutId);
  if (error) throw error;
}

export async function updateWorkoutFeedback(workoutId, { rpe, soreness, caloriesBurned }) {
  const update = {};
  if (rpe != null) update.rpe = rpe;
  if (soreness != null) update.soreness = soreness;
  if (caloriesBurned != null) update.calories_burned = caloriesBurned;
  if (Object.keys(update).length === 0) return;
  const { error } = await supabase.from("workouts").update(update).eq("id", workoutId);
  if (error) throw error;
}

// =========== RUNS ===========

export async function fetchRuns(userId) {
  const { data, error } = await supabase
    .from("runs")
    .select("*")
    .eq("user_id", userId)
    .order("timestamp", { ascending: false });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    distance: r.distance,
    unit: r.unit,
    duration: r.duration,
    pace: r.pace,
    timestamp: r.timestamp,
    runType: r.run_type || "Easy",
    elevation: r.elevation || 0,
    notes: r.notes,
  }));
}

export async function insertRun(userId, run) {
  validateRun(run);
  const { error } = await supabase.from("runs").insert({
    id: run.id,
    user_id: userId,
    distance: run.distance,
    unit: run.unit,
    duration: run.duration,
    pace: run.pace,
    timestamp: run.timestamp,
    run_type: run.runType || "Easy",
    elevation: run.elevation || 0,
    notes: run.notes,
  });
  if (error) throw error;
}

export async function deleteRun(runId) {
  const { error } = await supabase
    .from("runs")
    .delete()
    .eq("id", runId);
  if (error) throw error;
}

// =========== USER EXERCISES ===========

export async function fetchUserExercises(userId) {
  const { data, error } = await supabase
    .from("user_exercises")
    .select("*")
    .eq("user_id", userId);
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    name: r.name,
    muscle: r.muscle,
  }));
}

export async function insertUserExercise(userId, exercise) {
  const { error } = await supabase.from("user_exercises").insert({
    id: exercise.id,
    user_id: userId,
    name: exercise.name,
    muscle: exercise.muscle,
  });
  if (error) throw error;
}

// =========== MEAL LOG ===========

export async function fetchMealLog(userId) {
  // Limit to the last 60 days — enough for streak calculation (60 days)
  // and avoids loading unbounded historical data.
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 60);
  // meal_log.date is stored as a LOCAL date string; build the cutoff from local
  // date parts too (toISOString() is UTC and would truncate a day for some zones).
  const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, "0")}-${String(cutoff.getDate()).padStart(2, "0")}`;

  const { data, error } = await supabase
    .from("meal_log")
    .select("*")
    .eq("user_id", userId)
    .gte("date", cutoffStr);
  if (error) throw error;

  const log = {};
  const seen = new Set();
  for (const r of data || []) {
    if (seen.has(r.id)) continue; // skip any duplicate rows
    seen.add(r.id);
    if (!log[r.date]) log[r.date] = [];
    log[r.date].push({
      id: r.id,
      mealId: r.meal_id,
      ts: r.timestamp,
      servings: parseFloat(r.servings) || 1,
    });
  }
  return log;
}

export async function insertMealEntry(userId, date, entry) {
  validateMealEntry(date, entry);
  const { error } = await supabase.from("meal_log").insert({
    id: entry.id,
    user_id: userId,
    date,
    meal_id: entry.mealId,
    servings: entry.servings,
    timestamp: entry.ts,
  });
  if (error) throw error;
}

export async function updateMealServings(entryId, servings) {
  validateServings(servings);
  const { error } = await supabase
    .from("meal_log")
    .update({ servings })
    .eq("id", entryId);
  if (error) throw error;
}

export async function deleteMealEntry(entryId) {
  const { error } = await supabase
    .from("meal_log")
    .delete()
    .eq("id", entryId);
  if (error) throw error;
}

// =========== CUSTOM MEALS ===========

export async function fetchCustomMeals(userId) {
  const { data, error } = await supabase
    .from("custom_meals")
    .select("*")
    .eq("user_id", userId);
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    name: r.name,
    description: r.description,
    category: r.category,
    calories: r.calories,
    protein: r.protein,
    carbs: r.carbs,
    fat: r.fat,
    fiber: r.fiber,
  }));
}

export async function insertCustomMeal(userId, meal) {
  validateCustomMeal(meal);
  const { error } = await supabase.from("custom_meals").insert({
    id: meal.id,
    user_id: userId,
    name: meal.name,
    description: meal.description,
    category: meal.category,
    calories: meal.calories,
    protein: meal.protein,
    carbs: meal.carbs,
    fat: meal.fat,
    fiber: meal.fiber,
  });
  if (error) throw error;
}

export async function deleteCustomMeal(mealId) {
  const { error } = await supabase
    .from("custom_meals")
    .delete()
    .eq("id", mealId);
  if (error) throw error;
}

// =========== PERSONAL RECORDS ===========

export async function fetchPersonalRecords(userId) {
  const { data, error } = await supabase
    .from("personal_records")
    .select("*")
    .eq("user_id", userId);
  if (error) throw error;
  const records = {};
  for (const r of data || []) {
    records[r.exercise_id] = {
      weight: r.weight,
      reps: r.reps,
      date: r.date,
    };
  }
  return records;
}

export async function upsertPersonalRecord(userId, exerciseId, record) {
  const { error } = await supabase.from("personal_records").upsert({
    user_id: userId,
    exercise_id: exerciseId,
    weight: record.weight,
    reps: record.reps,
    date: record.date,
  });
  if (error) throw error;
}

export async function deletePersonalRecord(userId, exerciseId) {
  const { error } = await supabase
    .from("personal_records")
    .delete()
    .eq("user_id", userId)
    .eq("exercise_id", exerciseId);
  if (error) throw error;
}

// =========== USER TEMPLATES ===========

export async function fetchUserTemplates(userId) {
  const { data, error } = await supabase
    .from("user_templates")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    name: r.name,
    exerciseIds: r.exercise_ids || [],
  }));
}

export async function insertUserTemplate(userId, template) {
  const { error } = await supabase.from("user_templates").insert({
    id: template.id,
    user_id: userId,
    name: template.name,
    exercise_ids: template.exerciseIds,
  });
  if (error) throw error;
}

export async function deleteUserTemplate(templateId) {
  const { error } = await supabase
    .from("user_templates")
    .delete()
    .eq("id", templateId);
  if (error) throw error;
}

// =========== WEIGHT LOG ===========

export async function fetchWeightLog(userId) {
  const { data, error } = await supabase
    .from("weight_log")
    .select("*")
    .eq("user_id", userId)
    .order("date", { ascending: false });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    weight: r.weight,
    date: r.date,
  }));
}

export async function insertWeightEntry(userId, entry) {
  validateWeightEntry(entry);
  const { error } = await supabase.from("weight_log").insert({
    id: entry.id,
    user_id: userId,
    weight: entry.weight,
    date: entry.date,
  });
  if (error) throw error;
}

export async function deleteWeightEntry(entryId) {
  const { error } = await supabase
    .from("weight_log")
    .delete()
    .eq("id", entryId);
  if (error) throw error;
}

// =========== MACRO GOALS ===========

export async function updateMacroGoals(userId, goals) {
  const { error } = await supabase
    .from("profiles")
    .update({ macro_goals: goals })
    .eq("id", userId);
  if (error) throw error;
}

// =========== WEIGHT SETTINGS ===========

export async function updateWeightSettings(userId, settings) {
  const { error } = await supabase
    .from("profiles")
    .update({
      target_weight: settings.targetWeight ?? null,
      weight_reminder_time: settings.reminderTime ?? null,
    })
    .eq("id", userId);
  if (error) throw error;
}

// =========== MOOD CHECK-INS ===========

export async function insertMoodCheckin(userId, checkin) {
  const { data, error } = await supabase
    .from("mood_checkins")
    .insert({
      user_id: userId,
      date: checkin.date,
      mood: checkin.mood,
      energy: checkin.energy ?? null,
      note: checkin.note ?? null,
      context: checkin.context || "daily",
      workout_id: checkin.workoutId ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchMoodCheckins(userId) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 90);
  const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, "0")}-${String(cutoff.getDate()).padStart(2, "0")}`;

  const { data, error } = await supabase
    .from("mood_checkins")
    .select("*")
    .eq("user_id", userId)
    .gte("date", cutoffStr)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

// =========== TIMEZONE ===========

/**
 * Persist the device's IANA timezone on the profile so server endpoints and
 * crons can compute the user's local "today". Best-effort: failures are
 * swallowed so this never blocks app load.
 */
export async function syncTimezone(userId) {
  if (!userId) return;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return;
    await supabase.from("profiles").update({ timezone: tz }).eq("id", userId);
  } catch { /* non-fatal */ }
}

// =========== USER ONBOARDING ===========

export async function fetchOnboarding(userId) {
  const { data, error } = await supabase
    .from("user_onboarding")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    primaryGoal: data.primary_goal,
    experienceLevel: data.experience_level,
    trainingDaysPerWeek: data.training_days_per_week,
    sessionDuration: data.session_duration,
    equipment: data.equipment || [],
    limitations: data.limitations || [],
    additionalNotes: data.additional_notes,
    heightFeet: data.height_feet,
    heightInches: data.height_inches,
    weight: data.weight,
    age: data.age,
    sex: data.sex,
    onboardingCompleted: data.onboarding_completed,
  };
}

export async function saveOnboarding(userId, data) {
  const row = {
    primary_goal: data.primaryGoal,
    experience_level: data.experienceLevel,
    training_days_per_week: parseInt(data.trainingDaysPerWeek) || null,
    session_duration: data.sessionDuration,
    equipment: data.equipment || [],
    limitations: data.limitations || [],
    additional_notes: data.additionalNotes || null,
    height_feet: data.heightFeet ? parseInt(data.heightFeet) : null,
    height_inches: data.heightInches ? parseInt(data.heightInches) : null,
    weight: data.weight ? parseFloat(data.weight) : null,
    age: data.age ? parseInt(data.age) : null,
    sex: data.sex || null,
    onboarding_completed: true,
    updated_at: new Date().toISOString(),
  };

  // Try update first (row may exist from previous attempt)
  const { data: updated, error: updateError } = await supabase
    .from("user_onboarding")
    .update(row)
    .eq("user_id", userId)
    .select();

  if (updateError) throw updateError;

  // If no row existed, insert
  if (!updated || updated.length === 0) {
    const { error: insertError } = await supabase
      .from("user_onboarding")
      .insert({ user_id: userId, ...row });
    if (insertError) throw insertError;
  }
}

// =========== COACH CHATS ===========

export async function fetchRecentChats(userId, hoursBack = 48) {
  const since = new Date(Date.now() - hoursBack * 3600000).toISOString();
  const { data, error } = await supabase
    .from("coach_chats")
    .select("role, content, created_at")
    .eq("user_id", userId)
    .gte("created_at", since)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({ role: r.role, content: r.content }));
}

